import { AxiosError, AxiosResponse } from 'axios';
import instance, { ApiError } from '../../api/instance';
import { ContentCard, ContentCardLocale, NewContentCard } from './contents.types';
import { UploadFileToBucket } from 'src/api/utils/uploadFileToBucket';

const contentsApiPath = 'content/cards';

export type ContentQueryParams = {
  name?: string;
};

export const getContents = async ({
  params,
}: {
  params?: ContentQueryParams;
}): Promise<AxiosResponse<ContentCard[] & ApiError>> => {
  return instance
    .get<ContentCard[]>(contentsApiPath, { params })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const createContent = async (data: NewContentCard): Promise<AxiosResponse<ContentCard & ApiError>> => {
  return instance.post<ContentCard>(contentsApiPath, data).catch(e => e.response as AxiosError<ApiError> as never);
};

const contentApiPath = (id: string) => `content/cards/${id}`;

export const getContent = async ({
  contentId,
}: {
  contentId: string;
}): Promise<AxiosResponse<ContentCard & ApiError>> => {
  return instance.get<ContentCard>(contentApiPath(contentId)).catch(e => e.response as AxiosError<ApiError> as never);
};

export const updateContent = async ({
  contentId,
  data,
}: {
  contentId: string;
  data: Partial<ContentCard>;
}): Promise<AxiosResponse<ContentCard & ApiError>> => {
  return instance
    .post<ContentCard>(contentApiPath(contentId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const deleteContent = async ({
  contentId,
}: {
  contentId: string;
}): Promise<AxiosResponse<ContentCard & ApiError>> => {
  return instance
    .delete<ContentCard>(contentApiPath(contentId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export type UploadCardImage = (params: {
  contentId: string;
  data: { filename: string };
}) => Promise<AxiosResponse<Parameters<UploadFileToBucket>[0]>>;

const cardImageApiPath = (contentId: string) => `/content/cards/${contentId}/images`;

export const uploadCardImage: UploadCardImage = async ({ contentId, data }) => {
  return instance.post(cardImageApiPath(contentId), data).catch(e => e.response as never);
};

const cardLocalesApiPath = (contentId: string) => `/content/cards/${contentId}/translations`;

export const fetchContentLocalesAPI = async ({
  contentId,
}: {
  contentId: string;
}): Promise<AxiosResponse<ContentCardLocale[] & ApiError>> => {
  return instance.get(cardLocalesApiPath(contentId)).catch(e => e.response as never);
};

export const createContentLocaleAPI = async ({
  contentId,
  data,
}: {
  contentId: string;
  data: Omit<ContentCardLocale, 'id'>;
}): Promise<AxiosResponse<ContentCardLocale & ApiError>> => {
  return instance.post<ContentCardLocale>(cardLocalesApiPath(contentId), data).catch(e => e.response as never);
};

const cardLocaleApiPath = (contentId: string, localeId: string) =>
  `/content/cards/${contentId}/translations/${localeId}`;

export const updateContentLocaleAPI = async ({
  contentId,
  localeId,
  data,
}: {
  contentId: string;
  localeId: string;
  data: Omit<ContentCardLocale, 'id'>;
}): Promise<AxiosResponse<ContentCardLocale & ApiError>> => {
  return instance.post<ContentCardLocale>(cardLocaleApiPath(contentId, localeId), data).catch(e => e.response as never);
};

export const deleteContentLocaleAPI = async ({
  contentId,
  localeId,
}: {
  contentId: string;
  localeId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance.delete(cardLocaleApiPath(contentId, localeId)).catch(e => e.response as never);
};
